import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateProfileDto,
  type LeadFormFieldDto,
  type Profile,
  ProfileLanguage,
  type ProfileLeadFormDto,
  type ProfileModuleDto,
  ProfilesApi,
  ProfileVisibility,
  type UpdateProfileDto,
} from '@up2stay/up2promo-api-client';
import { useCallback } from 'react';

import { stripTemplatePolicyFromModules } from '@/core/lib/profile-module-entry-policies';
import type { ProfileStylingPayload } from '@/core/lib/profile-styling-form';
import { sanitizeModulesForProfileApi } from '@/core/lib/sanitize-profile-modules-for-api';
import { profileQueryKeys } from '@/core/query-keys/profile-query-keys';
import { useAuthStore } from '@/core/store/auth.store';

import { useCurrentWorkspace } from '../store/workspace.store';

// Re-export types from api-client for convenience
export type {
  CreateProfileDto,
  Profile,
  ProfileModuleDto,
  UpdateProfileDto,
} from '@up2stay/up2promo-api-client';

/**
 * Input data structure for creating a profile.
 * This matches the frontend form structure before conversion to API format.
 */
export interface CreateProfileInput {
  workspaceId?: string | null;
  visibility?: 'private' | 'unlisted';
  language?: 'auto' | 'de' | 'en';
  jobTitle?: string | null;
  company?: string | null;
  location?: string | null;
  title?: string | null;
  profileImage?: { id: string } | null;
  showProfileImage?: boolean;
  showUp2promoBranding?: boolean;
  modules?: ProfileModuleDto[];
  leadFormEnabled?: boolean;
  leadFormVisibilityMode?: 'off' | 'manual' | 'auto_open';
  leadFormAutoOpenDelay?: number;
  leadFormFields?: LeadFormFieldDto[];
  leadsAutoShareWithWorkspace?: boolean;
}

/**
 * Helper function to convert string values to the API's expected format.
 * The API client types expect multilingual objects, but we pass simple strings
 * which get serialized correctly by the API client.
 * Note: null values are converted to undefined since the API client types don't accept null.
 *
 * @param value - The string value to convert (can be string, null, or undefined)
 * @returns The value cast to the API client's expected type, or undefined if value is undefined or null
 */
function toApiValue(
  value: string | null | undefined,
): { [key: string]: unknown } | undefined {
  if (value === undefined || value === null) return undefined;
  // Cast string to API client's expected format (multilingual object)
  // The API client will serialize this correctly to a simple string
  return value as unknown as { [key: string]: unknown };
}

/**
 * Converts frontend profile input data to the API's CreateProfileDto format.
 * This ensures type safety and proper structure matching.
 *
 * Note: The API client types expect multilingual objects for some fields,
 * but the backend accepts simple strings. The API client handles serialization.
 */
function convertToCreateProfileDto(
  input: CreateProfileInput,
): Omit<CreateProfileDto, 'workspace'> & { workspace?: string } {
  const workspace =
    input.workspaceId && typeof input.workspaceId === 'string'
      ? input.workspaceId
      : undefined;

  const dto = {
    ...(workspace !== undefined && { workspace }),
    ...(input.visibility !== undefined && {
      visibility:
        input.visibility === 'private'
          ? ProfileVisibility.PRIVATE
          : ProfileVisibility.UNLISTED,
    }),
    ...(input.language !== undefined && {
      language:
        input.language === 'auto'
          ? ProfileLanguage.AUTO
          : input.language === 'de'
            ? ProfileLanguage.DE
            : ProfileLanguage.EN,
    }),
    ...(input.jobTitle !== undefined && {
      jobTitle: toApiValue(input.jobTitle),
    }),
    ...(input.company !== undefined && {
      company: toApiValue(input.company),
    }),
    ...(input.location !== undefined && {
      location: toApiValue(input.location),
    }),
    ...(input.title !== undefined && {
      title: toApiValue(input.title),
    }),
    ...(input.profileImage && {
      profileImage: { id: input.profileImage.id },
    }),
    ...(input.showProfileImage !== undefined && {
      showProfileImage: input.showProfileImage,
    }),
    ...(input.showUp2promoBranding !== undefined && {
      showUp2promoBranding: input.showUp2promoBranding,
    }),
    ...(input.leadsAutoShareWithWorkspace !== undefined && {
      leadsAutoShareWithWorkspace: input.leadsAutoShareWithWorkspace,
    }),
    ...(input.modules !== undefined && {
      modules: sanitizeModulesForProfileApi(
        stripTemplatePolicyFromModules(input.modules),
      ),
    }),
    ...(input.leadFormEnabled !== undefined && {
      leadForm: {
        enabled: input.leadFormEnabled,
        ...(input.leadFormVisibilityMode !== undefined && {
          visibilityMode: input.leadFormVisibilityMode,
        }),
        ...(input.leadFormAutoOpenDelay !== undefined && {
          autoOpenDelay: input.leadFormAutoOpenDelay,
        }),
        fields: input.leadFormFields ?? [],
        notificationEmails: [],
        notificationSms: [],
      } as ProfileLeadFormDto,
    }),
  };

  return dto;
}

/**
 * Input data structure for updating a profile.
 * This matches the frontend form structure before conversion to API format.
 */
export interface UpdateProfileInput {
  title?: string | null;
  visibility?: 'private' | 'unlisted';
  language?: 'auto' | 'de' | 'en';
  jobTitle?: string | null;
  company?: string | null;
  location?: string | null;
  showProfileImage?: boolean;
  showUp2promoBranding?: boolean;
  modules?: ProfileModuleDto[];
  leadFormEnabled?: boolean;
  leadFormVisibilityMode?: 'off' | 'manual' | 'auto_open';
  leadFormAutoOpenDelay?: number;
  leadFormFields?: LeadFormFieldDto[];
  leadsAutoShareWithWorkspace?: boolean;
  styling?: ProfileStylingPayload;
}

/**
 * Converts frontend profile update data to the API's UpdateProfileDto format.
 * This ensures type safety and proper structure matching, consistent with create operations.
 *
 * Note: The API client types expect multilingual objects for some fields,
 * but the backend accepts simple strings. The API client handles serialization.
 */
function convertToUpdateProfileDto(
  input: UpdateProfileInput,
): UpdateProfileDto {
  const modules =
    input.modules !== undefined
      ? sanitizeModulesForProfileApi(
          stripTemplatePolicyFromModules(input.modules),
        )
      : undefined;

  const dto: UpdateProfileDto = {
    ...(input.title !== undefined && {
      title: toApiValue(input.title),
    }),
    ...(input.visibility !== undefined && {
      visibility:
        input.visibility === 'private'
          ? ProfileVisibility.PRIVATE
          : ProfileVisibility.UNLISTED,
    }),
    ...(input.language !== undefined && {
      language:
        input.language === 'auto'
          ? ProfileLanguage.AUTO
          : input.language === 'de'
            ? ProfileLanguage.DE
            : ProfileLanguage.EN,
    }),
    ...(input.jobTitle !== undefined && {
      jobTitle: toApiValue(input.jobTitle),
    }),
    ...(input.company !== undefined && {
      company: toApiValue(input.company),
    }),
    ...(input.location !== undefined && {
      location: toApiValue(input.location),
    }),
    ...(input.showProfileImage !== undefined && {
      showProfileImage: input.showProfileImage,
    }),
    ...(input.showUp2promoBranding !== undefined && {
      showUp2promoBranding: input.showUp2promoBranding,
    }),
    ...(input.styling !== undefined && {
      styling: {
        layout: input.styling.layout,
        theme: input.styling.theme,
        headerStyle: input.styling.headerStyle,
        headerImage: input.styling.headerImage
          ? { id: input.styling.headerImage.id }
          : null,
        ...(input.styling.buttonShape
          ? { buttonShape: input.styling.buttonShape }
          : {}),
        ...(input.styling.accentColor
          ? { accentColor: input.styling.accentColor }
          : {}),
      } as UpdateProfileDto['styling'],
    }),
    ...(input.leadsAutoShareWithWorkspace !== undefined && {
      leadsAutoShareWithWorkspace: input.leadsAutoShareWithWorkspace,
    }),
    ...(modules !== undefined && {
      modules,
    }),
    ...(input.leadFormEnabled !== undefined && {
      leadForm: {
        enabled: input.leadFormEnabled,
        ...(input.leadFormVisibilityMode !== undefined && {
          visibilityMode: input.leadFormVisibilityMode,
        }),
        ...(input.leadFormAutoOpenDelay !== undefined && {
          autoOpenDelay: input.leadFormAutoOpenDelay,
        }),
        fields: input.leadFormFields ?? [],
        notificationEmails: [],
        notificationSms: [],
      } as ProfileLeadFormDto,
    }),
  };

  return dto;
}

// Re-export input types for convenience (already exported above as interfaces)

export function useProfile() {
  const user = useAuthStore((state) => state.user);
  const tokens = useAuthStore((state) => state.tokens);
  const hydrated = useAuthStore((state) => state.hydrated);
  const currentWorkspace = useCurrentWorkspace();
  const client = useQueryClient();

  const queryKey = profileQueryKeys.byUserAndWorkspace(
    user?.id,
    currentWorkspace?.id,
  );

  // Fetch profile using React Query (only when a workspace is selected)
  const {
    data: profile,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Profile | null> => {
      if (!currentWorkspace || !user || !tokens?.accessToken) {
        return null;
      }
      try {
        const response = await ProfilesApi.findByUserAndWorkspace({
          path: {
            userId: user.id,
            workspaceId: currentWorkspace.id,
          },
        });
        return response.data || null;
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled:
      !!hydrated && !!user && !!tokens?.accessToken && !!currentWorkspace,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Convert query error to Error type for backward compatibility
  const error = queryError
    ? queryError instanceof Error
      ? queryError
      : new Error(String(queryError))
    : null;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (updateData: UpdateProfileInput): Promise<Profile> => {
      if (!profile) {
        throw new Error('No profile to update');
      }

      // Convert frontend format to API format using type-safe conversion
      const body = convertToUpdateProfileDto(updateData);

      const response = await ProfilesApi.update({
        path: {
          id: profile.id,
        },
        body,
      });

      const updatedProfile = response.data;
      if (!updatedProfile) {
        throw new Error('Failed to update profile: no data returned');
      }

      return updatedProfile;
    },
    onSuccess: (updatedProfile: Profile) => {
      // Update cache with mutation result so ProfileEditPage and others see new data
      // without refetching (avoids form reset / flicker on edit page).
      client.setQueryData(queryKey, updatedProfile);
    },
  });

  // Create profile mutation (profiles are workspace-scoped only)
  const createProfileMutation = useMutation({
    mutationFn: async (createData: CreateProfileInput): Promise<Profile> => {
      if (!user) {
        throw new Error('User not found');
      }
      if (!currentWorkspace) {
        throw new Error('A workspace must be selected to create a profile');
      }

      const body = convertToCreateProfileDto(createData);
      const workspaceId = createData.workspaceId ?? currentWorkspace.id;
      const bodyWithWorkspace: CreateProfileDto = {
        ...body,
        workspace: workspaceId,
      };

      const response = await ProfilesApi.create({
        body: bodyWithWorkspace,
      });

      const newProfile = response.data;
      if (!newProfile) {
        throw new Error('Failed to create profile: no data returned');
      }

      return newProfile;
    },
    onSuccess: (newProfile: Profile) => {
      // Set cache immediately so ProfileEditPage never sees profile=null after create
      // (avoids brief "error" flash from invalidateQueries refetch).
      const workspaceId =
        (newProfile.workspace || currentWorkspace?.id) ?? null;
      client.setQueryData(
        profileQueryKeys.byUserAndWorkspace(user?.id, workspaceId),
        newProfile,
      );
    },
  });

  // Delete profile mutation
  const deleteProfileMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!profile) {
        throw new Error('No profile to delete');
      }

      await ProfilesApi.remove({
        path: {
          id: profile.id,
        },
      });
    },
    onSuccess: () => {
      // Invalidate queries to clear the profile from cache
      client.invalidateQueries({ queryKey });
    },
  });

  // Wrapper functions for backward compatibility
  const updateProfile = useCallback(
    async (updateData: UpdateProfileInput): Promise<Profile> => {
      return updateProfileMutation.mutateAsync(updateData);
    },
    [updateProfileMutation],
  );

  const createProfile = useCallback(
    async (createData: CreateProfileInput): Promise<Profile> => {
      return createProfileMutation.mutateAsync(createData);
    },
    [createProfileMutation],
  );

  const deleteProfile = useCallback(async (): Promise<void> => {
    return deleteProfileMutation.mutateAsync();
  }, [deleteProfileMutation]);

  return {
    profile: profile ?? null,
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
    updateProfile,
    createProfile,
    deleteProfile,
  };
}
